import { ClickAwayListener } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import * as S from './Configuration.styles';

export interface InfoTooltipProps {
  tooltip: React.ReactNode;
  ariaLabel: string;
  infoButtonDataTestId?: string;
  tooltipDataTestId?: string;
  width?: string;
}

const DEFAULTS = {
  button: 'form-management-info-button',
  tooltip: 'form-management-info-tooltip',
} as const;

const HOVER_CLOSE_DELAY_MS = 150;

export const InfoTooltip = ({
  tooltip,
  ariaLabel,
  infoButtonDataTestId,
  tooltipDataTestId,
  width = 'auto',
}: InfoTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const portaledTooltipRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) {
      return;
    }
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  const handleClickAway = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (portaledTooltipRef.current?.contains(e.target as Node)) {
        return;
      }
      clearCloseTimer();
      setIsOpen(false);
    },
    [clearCloseTimer],
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown">
      <S.InfoTooltipTrigger
        onMouseEnter={() => {
          clearCloseTimer();
          setIsOpen(true);
        }}
        onMouseLeave={scheduleClose}
      >
        <S.InfoTooltipAnchor ref={setAnchorEl}>
          <S.InfoTooltipButton
            type="button"
            data-testid={infoButtonDataTestId ?? DEFAULTS.button}
            aria-label={ariaLabel}
            aria-expanded={isOpen}
            onClick={() => {
              clearCloseTimer();
              setIsOpen((prev) => !prev);
            }}
          >
            <StyledIcon
              className="uil uil-info-circle"
              size="20px"
              color="currentColor"
            />
          </S.InfoTooltipButton>
        </S.InfoTooltipAnchor>
        {isOpen &&
          anchorEl &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={portaledTooltipRef}
              data-testid={tooltipDataTestId ?? DEFAULTS.tooltip}
              onMouseEnter={clearCloseTimer}
              onMouseLeave={scheduleClose}
            >
              <Tooltip variant="down" root={anchorEl} width={width}>
                <S.InfoTooltipMessage>{tooltip}</S.InfoTooltipMessage>
              </Tooltip>
            </div>,
            document.body,
          )}
      </S.InfoTooltipTrigger>
    </ClickAwayListener>
  );
};
